import React, { useEffect } from 'react';
import { Router } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import DateFnsUtils from '@date-io/date-fns';
import { SnackbarProvider } from 'notistack';
import Zoom from '@material-ui/core/Zoom';
import CancelPresentationIcon from '@material-ui/icons/CancelPresentation';
import {
  makeStyles,
  jssPreset,
  createStyles,
  ThemeProvider,
  StylesProvider,
} from '@material-ui/core/styles';
import { useDispatch } from 'react-redux';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { useJsApiLoader } from '@react-google-maps/api';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import {
  Auth,
  CacheProvider,
  useInitialTheme,
  Routes,
  CookiesNotification,
  GoogleAnalytics,
  ScrollReset,
  PORTAL_TYPE,
  setNotificationSettings,
  setApplicationDetails,
  setAlertSettings,
  SettingsNotification,
} from '@nsd/fe';
import { AppInsightsContext } from '@microsoft/applicationinsights-react-js';
import apps from 'src/apps';
import AppInsightsService from './telemetry/appInsights';
import {initClarity} from './telemetry/clarity';

const APP_VERSION = '2.0.0';

const APP_NAME = 'Admin Qore (Seed)';
const APP_END_USER = PORTAL_TYPE.EMPLOYEE;
/* Used to determine loading/splash screen logo
Choices for APP_INTERNAL_NAME
[
  "dispatch-qore",
  "lender-qore",
  "client-qore",
  "provider-qore",
  "claims-qore",
  "contact-qore",
  "insight-qore",
  "sales-qore",
  "marketing-qore",
  "market-qore",
  "developer-qore",
  "pricebook-qore",
  "underwriter-qore",
  "integration-qore",
  "admin-qore",
  "driver-qore",
  "billing-qore",
  "agent-qore",
  "dealer-qore",
  "licensing-qore",
  "ledger-qore",
  "data-qore",
  "notifications-qore"
]
*/
const APP_INTERNAL_NAME = 'admin-qore';
const alertsConfigObject = {
  actionButtonContent: CancelPresentationIcon,
  // Used standard or filled
  variant: 'filled',
  position: 'top',
  transitionComponent: Zoom,
};

const notificationsConfigObject = {
  pulseAnimation: true,
  displayNumberedBadge: true,
};

const history = createBrowserHistory();
const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
const GOOGLE_SCRIPT_LIBRARIES = ['drawing,places'];
const useStyles = makeStyles(() => createStyles({
  '@global': {
    '*': {
      boxSizing: 'border-box',
      margin: 0,
      padding: 0,
    },
    html: {
      '-webkit-font-smoothing': 'antialiased',
      '-moz-osx-font-smoothing': 'grayscale',
      height: '100%',
      width: '100%',
    },
    body: {
      height: '100%',
      width: '100%',
    },
    '#root': {
      height: '100%',
      width: '100%',
    },
  },
}));

const openSettingsWidget = () => {
  const node = document.querySelector('[title="Settings"]');
  node.click();
};

function App() {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(
      setApplicationDetails(
        APP_END_USER,
        APP_NAME,
        APP_VERSION,
        APP_INTERNAL_NAME
      )
    );
  }, [dispatch]);
  // Set alert and notification preferences / settings
  useEffect(() => {
    dispatch(setAlertSettings(alertsConfigObject));
    dispatch(setNotificationSettings(notificationsConfigObject));
  }, [dispatch]);
  useStyles();
  const { theme } = useInitialTheme();
  // Will silently fail without a Google API key
  useJsApiLoader({
    id: 'google-maps',
    libraries: GOOGLE_SCRIPT_LIBRARIES,
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });

  /* initialize Application Insights
  * https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript?tabs=snippet
  * https://docs.microsoft.com/en-us/azure/azure-monitor/app/javascript-react-plugin
  *
  * Tracking custom telemetry events or metric is made available to all components via
  * AppInsightsContext
  */
  const appInsightsService = new AppInsightsService();

  appInsightsService.initialize(
    process.env.REACT_APP_APPINSIGHTS_KEY,
    history,
    process.env.REACT_APP_AI_CLOUD_ROLE_NAME
  );

  initClarity();

  if (!theme) return null;
  return (
    <AppInsightsContext.Provider value={appInsightsService.reactPlugin}>
      <CacheProvider>
        <DndProvider backend={HTML5Backend}>
          <ThemeProvider theme={theme}>
            <StylesProvider jss={jss}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <SnackbarProvider maxSnack={1}>
                  <Router history={history}>
                    <Auth>
                      <ScrollReset />
                      <GoogleAnalytics />
                      <CookiesNotification />
                      <SettingsNotification
                        settingsPathFunc={openSettingsWidget}
                      />
                      <Routes
                        apps={apps || []}
                      />
                    </Auth>
                  </Router>
                </SnackbarProvider>
              </MuiPickersUtilsProvider>
            </StylesProvider>
          </ThemeProvider>
        </DndProvider>
      </CacheProvider>
    </AppInsightsContext.Provider>
  );
}

export default App;
